/**
 * Document print orchestration — single or bulk — reusable across tabs.
 * Opens a PrintShell preview tab (same chrome as Patient clinical print).
 */
import { request } from "../utils/api";
import { getPrintConfig } from "./documentPrintRegistry";
import {
  buildEvaluationPrintHtml,
  fetchEvaluationForPrint,
  normalizeEvaluationRole,
} from "./forms/evaluationPrint";
import { buildServiceLogPrintHtml } from "./forms/serviceLogPrint";
import {
  BULK_PRINT_FETCH_CONCURRENCY,
  mapWithConcurrency,
} from "../utils/mapWithConcurrency";

/** Storage key prefix for DocumentsPrintPreviewPage (localStorage + sessionStorage). */
export const DOCUMENTS_PRINT_STORAGE_PREFIX = "kv-documents-print:";

/**
 * In-module cache so React Strict Mode remount in the preview tab still finds
 * the payload after storage keys are cleared on first read.
 * @type {Map<string, { documents: Array<{html:string,title?:string}>, title?: string, subtitle?: string }>}
 */
const printPayloadMemory = new Map();

/**
 * @param {unknown} row
 */
export function isDocumentCompleted(row) {
  const s = String(row?.detail?.status || row?.status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  return s === "completed";
}

/**
 * Evaluation detail id — Documents tab uses `detail.id`; Evaluation History uses `id`.
 * @param {Record<string, unknown>} row
 */
export function getEvaluationDetailId(row) {
  return row?.detail?.id ?? row?.id ?? null;
}

/**
 * Whether a selected row can be included in bulk print.
 * Status is NOT required — pending / incomplete forms print as blank layouts.
 *
 * @param {unknown} row
 * @param {{ kind?: 'form' | 'evaluation' | 'service_log' }} [opts]
 */
export function isRowPrintable(row, opts = {}) {
  if (!row) return false;
  if (opts.kind === "service_log") {
    return row.id != null;
  }
  if (opts.kind === "evaluation") {
    const role =
      row.type || row.detail?.type || row.evaluationType || null;
    // Allow blank template print even when no saved evaluation detail exists.
    return getEvaluationDetailId(row) != null || Boolean(role);
  }
  return Boolean(row.formKey && getPrintConfig(row.formKey));
}

/**
 * Stable selection key for a document list row.
 * @param {Record<string, unknown>} row
 * @param {number} [index]
 */
export function getDocumentRowKey(row, index = 0) {
  return String(
    row._rowKey ??
      row.detail?.id ??
      row._id ??
      row.id ??
      `${row.formKey || "row"}-${index}`,
  );
}

/**
 * @param {{
 *   companyName?: string,
 *   logoUrl?: string,
 *   seniorStaffNurse?: string,
 * }} branding
 * @param {boolean} [needsSeniorStaffNurse]
 */
function buildBranding(branding, needsSeniorStaffNurse) {
  const base = {
    companyName: branding.companyName || "Company",
    logoUrl: branding.logoUrl || "",
  };
  if (needsSeniorStaffNurse) {
    base.seniorStaffNurse = branding.seniorStaffNurse || "";
  }
  return base;
}

function blankRecordFromContext(ctx) {
  const name = String(ctx.employeeName || "").trim();
  return {
    employeeName: name,
    applicantName: name,
    userId: ctx.selectedUserId,
  };
}

/**
 * Resolve admin-viewing-other-employee userId query for form GETs.
 * @param {{
 *   isAdminRole?: boolean,
 *   selectedUserId?: string|number|null,
 *   sessionUserId?: string|number|null,
 * }} ctx
 */
export function getViewerUserIdForPrint(ctx) {
  const adminViewingOther =
    ctx.isAdminRole &&
    ctx.selectedUserId != null &&
    ctx.sessionUserId != null &&
    String(ctx.selectedUserId) !== String(ctx.sessionUserId);
  return adminViewingOther ? ctx.selectedUserId : null;
}

/**
 * Build print HTML for one application/medical form row.
 * Pending / missing detail → blank form layout (same design as fill form).
 * @returns {Promise<{ html: string, title: string }|null>}
 */
export async function buildFormPrintDocument(row, ctx) {
  const formKey = row.formKey;
  const config = getPrintConfig(formKey);
  if (!config) return null;

  const detailId = row.detail?.id;
  let record = null;
  if (detailId) {
    try {
      const viewerUserId = getViewerUserIdForPrint(ctx);
      const path = config.getPath(detailId, viewerUserId);
      const response = await request(path);
      record = config.parse(response, ctx.selectedUserId);
    } catch (err) {
      console.warn(
        "Document print fetch failed; using blank layout:",
        formKey,
        err,
      );
    }
  }

  const branding = buildBranding(
    ctx.branding || {},
    config.needsSeniorStaffNurse,
  );
  const safeRecord = record || blankRecordFromContext(ctx);

  try {
    const html = config.build(safeRecord, branding);
    if (!html || !String(html).trim()) {
      return {
        html: blankPlaceholderHtml(
          row.name || config.label || formKey,
          branding.companyName,
        ),
        title: row.name || config.label || formKey,
      };
    }
    return {
      html,
      title: row.name || config.label || formKey,
    };
  } catch (err) {
    console.warn("Document print build failed; using blank sheet:", formKey, err);
    return {
      html: blankPlaceholderHtml(
        row.name || config.label || formKey,
        branding.companyName,
      ),
      title: row.name || config.label || formKey,
    };
  }
}

function blankPlaceholderHtml(title, companyName) {
  const t = String(title || "Document")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const c = String(companyName || "Company")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${t}</title>
<style>
  body{font-family:system-ui,sans-serif;color:#111827;padding:24px}
  h1{font-size:18px;margin:0 0 8px}
  p{font-size:12px;color:#64748b;margin:0}
  .box{margin-top:20px;border:1px dashed #cbd5e1;border-radius:8px;min-height:320px;padding:16px;color:#94a3b8;font-size:12px}
</style></head><body>
  <h1>${t}</h1>
  <p>${c}</p>
  <div class="box">No saved data for this form. Fields are blank for print.</div>
</body></html>`;
}

/**
 * Build print HTML for one evaluation row.
 * @returns {Promise<{ html: string, title: string }|null>}
 */
export async function buildEvaluationPrintDocument(row, ctx) {
  const detailId = getEvaluationDetailId(row);
  const evalRole =
    row.type ||
    row.detail?.type ||
    row.evaluationType ||
    ctx.positionSlug;
  if (!evalRole) return null;

  const employeeName = row.employee
    ? `${row.employee.firstName || ""} ${row.employee.lastName || ""}`.trim()
    : ctx.employeeName || "";

  let payload = null;
  if (detailId) {
    try {
      payload = await fetchEvaluationForPrint(evalRole, detailId, (path) =>
        request(path),
      );
    } catch (err) {
      console.warn(
        "Evaluation print fetch failed; using blank layout:",
        evalRole,
        err,
      );
    }
  }

  // Missing / pending evaluation → plain empty layout (same structure as filled).
  if (!payload) {
    const role = normalizeEvaluationRole(evalRole);
    payload = {
      role,
      mapped: {
        employeeLabel: employeeName || "",
      },
      backend: {},
    };
  }

  const html = buildEvaluationPrintHtml(
    payload.role,
    payload.mapped,
    payload.backend,
    buildBranding(ctx.branding || {}),
  );

  const title =
    row.name ||
    [row.type, "evaluation", employeeName ? `— ${employeeName}` : ""]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "Evaluation";

  return { html, title };
}

/**
 * @param {Array<Record<string, unknown>>} entries
 * @param {object} ctx
 */
export function buildServiceLogListPrintDocument(entries, ctx) {
  const html = buildServiceLogPrintHtml(entries, {
    companyName: ctx.branding?.companyName,
    logoUrl: ctx.branding?.logoUrl,
    employeeName: ctx.employeeName,
    employeeEmail: ctx.employeeEmail,
  });
  return {
    html,
    title: "Service Log",
  };
}

/**
 * Fetch + build HTML for many rows; compose into one PrintShell session.
 *
 * @param {Array<Record<string, unknown>>} rows
 * @param {{
 *   kind: 'form' | 'evaluation',
 *   branding: object,
 *   selectedUserId?: string|number,
 *   sessionUserId?: string|number,
 *   isAdminRole?: boolean,
 *   positionSlug?: string,
 *   documentTitle?: string,
 *   employeeName?: string,
 * }} ctx
 * @returns {Promise<{ html: string, documents: Array<{html:string,title:string}>, printed: number, failed: Array<{ name: string, reason: string }> }>}
 */
export async function buildBulkPrintHtml(rows, ctx) {
  const list = Array.isArray(rows) ? rows : [];
  const results = await mapWithConcurrency(
    list,
    BULK_PRINT_FETCH_CONCURRENCY,
    async (row) => {
      const employeeName = row.employee
        ? `${row.employee.firstName || ""} ${row.employee.lastName || ""}`.trim()
        : ctx.employeeName || "";
      const name =
        row.name ||
        [row.type, employeeName].filter(Boolean).join(" — ") ||
        row.formKey ||
        "Document";
      try {
        const doc =
          ctx.kind === "evaluation"
            ? await buildEvaluationPrintDocument(row, ctx)
            : await buildFormPrintDocument(row, ctx);
        if (!doc?.html) {
          return {
            ok: false,
            name,
            reason: "Print layout unavailable for this form",
          };
        }
        return { ok: true, doc };
      } catch (err) {
        console.error("Bulk print item failed:", name, err);
        return {
          ok: false,
          name,
          reason: err?.message || "Failed to load",
        };
      }
    },
  );

  const documents = [];
  const failed = [];
  results.forEach((result) => {
    if (!result) return;
    if (result.ok && result.doc) {
      documents.push(result.doc);
    } else if (!result.ok) {
      failed.push({ name: result.name, reason: result.reason });
    }
  });

  return {
    html: documents[0]?.html || "",
    documents,
    printed: documents.length,
    failed,
  };
}

/**
 * Persist print payload for the preview tab.
 * Uses localStorage (shared across tabs) + sessionStorage fallback, and an
 * in-module memory map so Strict Mode remounts still work after consume.
 *
 * @param {string} token
 * @param {{ documents: Array<{html:string,title?:string}>, title?: string, subtitle?: string }} payload
 * @returns {boolean}
 */
function writeDocumentsPrintPayload(token, payload) {
  const key = `${DOCUMENTS_PRINT_STORAGE_PREFIX}${token}`;
  const raw = JSON.stringify(payload);
  printPayloadMemory.set(token, payload);
  let wrote = false;
  try {
    localStorage.setItem(key, raw);
    wrote = true;
  } catch (err) {
    console.warn("localStorage print payload failed", err);
  }
  try {
    sessionStorage.setItem(key, raw);
    wrote = true;
  } catch (err) {
    console.warn("sessionStorage print payload failed", err);
  }
  return wrote;
}

/**
 * Read print payload in the preview tab. Safe under React Strict Mode
 * (second mount reuses in-memory copy after storage keys were cleared).
 *
 * @param {string} token
 * @returns {{ documents: Array<{html:string,title?:string}>, title?: string, subtitle?: string }|null}
 */
export function consumeDocumentsPrintPayload(token) {
  if (!token) return null;
  if (printPayloadMemory.has(token)) {
    return printPayloadMemory.get(token);
  }
  const key = `${DOCUMENTS_PRINT_STORAGE_PREFIX}${token}`;
  try {
    const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    printPayloadMemory.set(token, parsed);
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    return parsed;
  } catch (err) {
    console.error("Failed to read print payload", err);
    return null;
  }
}

/**
 * Open PrintShell preview tab with one sheet per document (page-break on print).
 * @param {Array<{ html: string, title?: string }>} documents
 * @param {{ title?: string, subtitle?: string }} [meta]
 * @returns {boolean}
 */
export function openDocumentsPrintTab(documents, meta = {}) {
  const list = Array.isArray(documents)
    ? documents.filter((d) => d?.html && String(d.html).trim())
    : [];
  if (!list.length) return false;

  const token = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const key = `${DOCUMENTS_PRINT_STORAGE_PREFIX}${token}`;
  const payload = {
    documents: list,
    title:
      meta.title ||
      `Print ${list.length} form${list.length === 1 ? "" : "s"}`,
    subtitle: meta.subtitle || "",
  };

  if (!writeDocumentsPrintPayload(token, payload)) {
    console.error("Failed to store print payload");
    return false;
  }

  const url = `/documents/print-preview?token=${encodeURIComponent(token)}`;
  const printWindow = window.open(url, "_blank");
  if (!printWindow) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    printPayloadMemory.delete(token);
    return false;
  }
  return true;
}

/**
 * Fetch selected service log entries from backend print-bundle, then print list.
 * Falls back to client-side entries if the endpoint is unavailable.
 *
 * @param {{
 *   employeeId: string|number,
 *   ids: Array<string|number>,
 *   entriesFallback: Array<Record<string, unknown>>,
 *   branding: object,
 *   employeeName?: string,
 *   employeeEmail?: string,
 * }} args
 */
export async function printSelectedServiceLogs(args) {
  let entries = args.entriesFallback || [];
  try {
    const response = await request("/service-logs/print-bundle", {
      method: "POST",
      body: JSON.stringify({
        employeeId: Number(args.employeeId),
        ids: args.ids.map((id) => Number(id)),
      }),
    });
    if (response?.status === 1 && Array.isArray(response.result?.serviceLogs)) {
      entries = response.result.serviceLogs;
    }
  } catch (err) {
    console.warn("service-logs/print-bundle unavailable, using local rows", err);
  }

  const doc = buildServiceLogListPrintDocument(entries, {
    branding: args.branding,
    employeeName: args.employeeName,
    employeeEmail: args.employeeEmail,
  });
  if (!doc.html) return { ok: false, printed: 0 };
  const ok = openDocumentsPrintTab([doc], {
    title: "Service Log",
    subtitle: args.employeeName || "",
  });
  return { ok, printed: entries.length };
}
